/**
 * Formatting shared by the response-time strip, the table column and the
 * answers drawer, so the same interval never reads two different ways.
 */

/**
 * A minute count as the shortest thing a person can read at a glance.
 *
 * Deliberately coarse above an hour: "2h 15m" is as much precision as anyone
 * acts on, and "1d 4h" beats "1720m" for a lead that went cold over a weekend.
 */
export const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined || isNaN(minutes)) return "—";
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;

  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
};

/**
 * One CSV cell.
 *
 * The leading apostrophe on =, +, - and @ is not decoration: Excel and Sheets
 * treat a cell starting with those as a formula, so a prospect who types
 * "=cmd|..." into a form field would otherwise have it execute on whoever opens
 * the export. Everything is quoted and inner quotes doubled, per RFC 4180.
 */
const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

/**
 * The filtered rows as a CSV string.
 *
 * Answer columns are derived from the rows themselves rather than fixed, since
 * every Meta form asks different questions — a hard-coded header would quietly
 * drop whatever marketing added last week. Columns appear in the order first
 * encountered so a single-form export keeps the order of the form itself.
 */
export const rowsToCsv = (rows = []) => {
  const answerLabels = [];
  rows.forEach((row) => {
    (row.answers || []).forEach((answer) => {
      if (!answerLabels.includes(answer.label)) answerLabels.push(answer.label);
    });
  });

  const header = [
    "Status",
    "Name",
    "Phone",
    "Returning client",
    "Form",
    "Project",
    "Submitted on Meta",
    "Assigned to",
    "Lead ID",
    "Minutes to first contact",
    "Error",
    ...answerLabels,
  ];

  const lines = rows.map((row) => {
    const byLabel = new Map((row.answers || []).map((a) => [a.label, a.value]));
    return [
      row.status,
      row.name,
      // Kept as text: a phone number that loses its leading digits to numeric
      // coercion is worse than useless.
      row.phone,
      row.isExistingClient ? "yes" : "no",
      row.formName,
      row.projectName,
      row.submittedAt,
      row.assignedTo?.name,
      row.leadID,
      row.minutesToFirstContact,
      row.error,
      ...answerLabels.map((label) => byLabel.get(label) ?? ""),
    ]
      .map(csvCell)
      .join(",");
  });

  // BOM so Excel opens UTF-8 correctly — without it Pakistani names and any
  // non-ASCII answer render as mojibake on Windows.
  return `﻿${[header.map(csvCell).join(","), ...lines].join("\r\n")}`;
};

/** Triggers a browser download of `content` as `filename`. */
export const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * How a response interval should read: fine, late, or nobody has called yet.
 *
 * `waiting` is separated from `late` on purpose — a lead answered after two
 * hours is a past miss, while one still unanswered after two hours is work
 * sitting on the floor right now.
 */
export const responseTone = ({ minutesToFirstContact, minutesWaiting, slaMinutes = 60 }) => {
  if (minutesToFirstContact !== null && minutesToFirstContact !== undefined) {
    return minutesToFirstContact <= slaMinutes ? "ok" : "late";
  }
  if (minutesWaiting !== null && minutesWaiting !== undefined) {
    return minutesWaiting > slaMinutes ? "overdue" : "waiting";
  }
  return "none";
};
